import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListTableShell } from "@/components/ResourceShell";
import { ListToolbar } from "@/components/ListToolbar";
import { LicenseStatusBadge, TrialBadge } from "@/components/StatusBadges";
import { CreateLicenseDialog } from "@/components/dialogs/CreateLicenseDialog";
import { useLicenses } from "@/api/queries/licenses";
import { useCustomers } from "@/api/queries/customers";

export function LicensesList() {
  const licenses = useLicenses();
  const customers = useCustomers();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const customerName = new Map((customers.data ?? []).map((c) => [c.id, c.name]));
  const all = licenses.data ?? [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter(
      (l) =>
        l.edition.toLowerCase().includes(term) ||
        l.id.toLowerCase().includes(term) ||
        (customerName.get(l.customerId) ?? "").toLowerCase().includes(term),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, q, customers.data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text3">
          {filtered.length} von {all.length} Lizenzen
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-license-button">
          <Plus className="h-4 w-4" /> Lizenz anlegen
        </Button>
      </div>

      <ListToolbar search={q} onSearch={setQ} placeholder="Lizenz suchen…" />

      <ListTableShell
        isLoading={licenses.isLoading}
        isError={licenses.isError}
        errorMessage={licenses.error?.message}
        isEmpty={filtered.length === 0}
        emptyMessage={all.length === 0 ? "Noch keine Lizenzen" : "Keine Treffer"}
        onRetry={() => licenses.refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Edition</TableHead>
              <TableHead>Kunde</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Gueltig bis</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((l) => (
              <TableRow key={l.id} data-clickable="true" onClick={() => navigate(`/licenses/${l.id}`)}>
                <TableCell className="font-medium">
                  {l.edition} {l.trial && <TrialBadge />}
                </TableCell>
                <TableCell className="text-text2">
                  {customerName.get(l.customerId) ?? <span className="mono text-xs text-text3">{l.customerId}</span>}
                </TableCell>
                <TableCell>
                  <LicenseStatusBadge status={l.status} />
                </TableCell>
                <TableCell className="text-text3">{l.validUntil.slice(0, 10)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>

      {createOpen && <CreateLicenseDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </div>
  );
}
